(function(module) {

    "use strict";

    // -------------------------------------------------------------------------------------------
    // OO Utils
    // -------------------------------------------------------------------------------------------
    
    var __hasOwn = {}.hasOwnProperty;

    function extend(child, parent) {
        for (var key in parent) {
            if (__hasOwn.call(parent, key)) {
                child[key] = parent[key];
            }
        }
        function Ctor() {
            this.constructor = child;
        }
        Ctor.prototype = parent.prototype;
        child.prototype = new Ctor();
        child.__super__ = parent.prototype;
        return child;
    }

    // -------------------------------------------------------------------------------------------
    // General Utils
    // -------------------------------------------------------------------------------------------

    function flatMap(array) {
        return array.reduce(function(flat, array) {
            return flat.concat(array);
        }, []);
    }


    // -------------------------------------------------------------------------------------------
    // General Utils
    // -------------------------------------------------------------------------------------------
    
    /**
     * A general function for doing an AND filter
     * 
     * @param  {Array[Array[String]]} ids the IDs to filter
     * @return {Array[String]} the filtered IDs
     */
    function andFilter(ids) {
        // The filtering algorithm

        // 1. count the number of applied filter groups
        var groupCount = ids.length;

        // 2. flat map the filters to get an array of IDs
        var flat = flatMap(ids);

        // 3. count the number of times each ID appears in the flattened list
        var collector = flat.reduce(function(collector, id) {
            collector[id] = collector[id] != null ? collector[id] + 1 : 1;
            return collector;
        }, {});

        // 4. if the hotel appears in the flattened list the same number of times as the group count, then it should appear
        return Object.keys(collector).reduce(function(ids, id) {
            return ids.concat(collector[id] === groupCount ? [id] : []);
        }, []);
    }

    /**
     * A general or function
     * 
     * @param  {Array[Array[String]]} ids the IDs to filter
     * @return {Array[String]} the filtered IDs
     */
    function orFilter(ids) {
        var flat = flatMap(ids);
        return Object.keys(flat.reduce(function(collector, next) {
            collector[next] = true;
            return collector;
        }, Object.create(null)));
    }


    // -------------------------------------------------------------------------------------------
    // Abstract filter
    // -------------------------------------------------------------------------------------------
    
    /**
     * The abstract filter
     * 
     * @param {String} name The name of the filter
     * @param {String, Array[String]} hotelIds The hotel IDs that apply for the filter
     * @param {FilterGroup} group The filter group that the filter is part of
     */
    function AbstractFilter(name) {
        this.name = name;
        this.disabled = false;
        this._group = null;
    }

    /**
     * Notifies the mediator of a filter change
     * 
     * @return {Filter} the filter
     */
    AbstractFilter.prototype.change = function() {
        this._group.change();
        return this;
    };

    /**
     * Visits the filter in the tree
     * 
     * @param {Function} visitor the visitor function
     * @return {Function} the visitor function
     */
    AbstractFilter.prototype.visit = function(visitor) {
        visitor.acceptFilter && visitor.acceptFilter(this);
        return visitor;
    };

    /**
     * Sets the filter group
     *
     * @param {FilterGroup} group the filter group
     * @return {Filter} the filter
     */
    AbstractFilter.prototype._setGroup = function(group) {
        this._group = group;
        return this;
    };

    /**
     * Resets the filter
     * 
     * @return {Filter} the filter
     */
    AbstractFilter.prototype._reset = function() {
        this.disabled = false;
        return this;
    };

    /**
     * Collects all the IDs for the filter group
     * 
     * @return {Array[String]} the IDs for the filter group
     */
    AbstractFilter.prototype._collect = function() {
        return [];
    };

    /**
     * Determines if the filter is applied
     * 
     * @return {Boolean} true if the filter is applied, false otherwise
     */
    AbstractFilter.prototype._isFilterApplied = function() {
        return false;
    };

    /**
     * Calculates the filtered count
     * 
     * @param  {Array[String]} filteredIds the filtered IDs
     * @return {AbstractFilter} the filter
     */
    AbstractFilter.prototype._calculate = function(filteredIds) {
    };


    // -------------------------------------------------------------------------------------------
    // Filter Group
    // -------------------------------------------------------------------------------------------

    /**
     * Represents a group filters
     * 
     * @param {String} type the type of the filter (e.g. `check` or `text`)
     * @param {String} name the name of the filter group
     * @param {Filters} filters the filters in this group
     * @param {String} mode the mode of the filter group (e.g. AND or OR )
     */
    function FilterGroup(type, name, mode, filters) {
        this.type = type;
        this.name = name;
        this.filters = filters;
        this._mode = mode;
        this._mediator = null;
    }

    /**
     * The filter group modes
     * 
     */
    FilterGroup.MODE = {};
    FilterGroup.MODE.AND = "AND";
    FilterGroup.MODE.OR = "OR";

    /**
     * Informs the filters that a filter has been checked
     * 
     * @return {FilterGroup} the filter group
     */
    FilterGroup.prototype.change = function() {
        this._mediator.change();
        return this;
    };

    /**
     * Resets all the filters in the filter group
     * 
     * @return {AbstractFilterGroup} the filters
     */
    FilterGroup.prototype.reset = function() {
        this.filters.forEach(function(filter) {
            filter._reset();
        });
        return this;
    };

    /**
     * Determines if a filter is applied within the filter group
     * 
     * @return {AbstractFilterGroup} the filters
     */
    FilterGroup.prototype.isFilterApplied = function() {
        return this.filters.some(function(filter) {
            return filter._isFilterApplied();
        });
    };

    /**
     * Visits each node in the tree
     * 
     * @param {Function} visitor the visitor function
     * @return {Function} the visitor function
     */
    FilterGroup.prototype.visit = function(visitor) {
        visitor.acceptGroup && visitor.acceptGroup(this);
        this.filters.forEach(function(filter) {
            filter.visit(visitor);
        });
        return visitor;
    };

    /**
     * Sets the mediator
     *
     * @param  {FilterMediator} mediator the mediator
     * @return {FilterGroup} the filter group
     */
    FilterGroup.prototype._setMediator = function(mediator) {
        this._mediator = mediator;
        return this;
    };

    /**
     * Collects all the IDs for the filter group
     * 
     * @return {Array[String]} the IDs for the filter group
     */
    FilterGroup.prototype._collect = function() {
        var collected = this.filters.reduce(function(collector, filter) {
            collector.push(filter._collect());
            return collector;
        }, []);

        collected = collected.filter(function(filter) {
            return filter.length > 0;
        });
        
        collected = this._mode === FilterGroup.MODE.OR ? orFilter(collected) : andFilter(collected);
        return Object.keys(collected.reduce(function(collector, next) {
            collector[next] = true;
            return collector;
        }, Object.create(null)));
    };

    /**
     * Calculates the filters in the group
     * 
     * @param  {Array[String]} filteredIds the filtered IDs
     * @return {FilterGroup} the filter group
     */
    FilterGroup.prototype._calculate = function(filteredIds) {
        this.filters.forEach(function(filter) {
            filter._calculate(filteredIds);
        });
    };


    // -------------------------------------------------------------------------------------------
    // Hierarchy Filter Group
    // -------------------------------------------------------------------------------------------

    /**
     * Represents a group of {HierarchyFilter} filters
     * 
     * @extends {FilterGroup}
     * @param {String} type the type of the filter (e.g. `check` or `text`)
     * @param {String} name the name of the filter group
     * @param {Filters} filters the filters in this group
     * @param {String} mode the mode of the filter group (e.g. AND or OR )
     */
    function HierarchyFilterGroup(type, name, mode, filters) {
        HierarchyFilterGroup.__super__.constructor.call(this, type, name, mode, filters);
    }
    extend(HierarchyFilterGroup, FilterGroup);

    /**
     * Collects all the IDs for the hierarchy filter group
     * 
     * @return {Array[String]} the IDs for the filter group
     */
    HierarchyFilterGroup.prototype._collect = function () {
        var collected = flatMap(this.filters.reduce(function(collector, filter) {
            collector.push(filter._collect());
            return collector;
        }, []));
        collected = this._mode === FilterGroup.MODE.OR ? orFilter(collected) : andFilter(collected);
        return Object.keys(collected.reduce(function(collector, next) {
            collector[next] = true;
            return collector;
        }, Object.create(null)));
    };


    // -------------------------------------------------------------------------------------------
    // CheckBox filter
    // -------------------------------------------------------------------------------------------

    /**
     * The checkbox filter
     * 
     * @param {String} name       the name of the filter to display in the view
     * @param {Array[String]} ids the array of hotel IDs that this filter applies to
     * @param {FilterGroup} group the filter group
     */
    function CheckBoxFilter(name, ids) {
        CheckBoxFilter.__super__.constructor.apply(this, [name]);
        this.checked = false;
        this.count = ids.length;
        this.filteredCount = ids.length;
        this._ids = ids;
    }
    extend(CheckBoxFilter, AbstractFilter);

    /**
     * Resets the filter
     * 
     * @return {CheckBoxFilter} the filter
     */
    CheckBoxFilter.prototype._reset = function() {
        this.checked = false;
        this.filteredCount = this._ids.length;
        return CheckBoxFilter.__super__._reset.apply(this, arguments);
    };

    /**
     * Collects all the IDs for the filter group
     * 
     * @return {Array[String]} the IDs for the filter group
     */
    CheckBoxFilter.prototype._collect = function() {
        return this.checked ? this._ids : [];
    };

    /**
     * Determines if the filter is applied
     * 
     * @return {Boolean} true if the filter is applied, false otherwise
     */
    CheckBoxFilter.prototype._isFilterApplied = function() {
        return this.checked;
    };

    /**
     * Calculates the filtered count
     * 
     * @param  {Array[String]} filteredIds the filtered IDs
     * @return {CheckBoxFilter} the filter
     */
    CheckBoxFilter.prototype._calculate = function(filteredIds) {
        this.filteredCount = this._ids.filter(function(id) {
            return filteredIds.indexOf(id) > -1;
        }).length;
        this.disabled = this.filteredCount === 0;
        return this;
    };

    
    // -------------------------------------------------------------------------------------------
    // Location Hierarchy Filter
    // -------------------------------------------------------------------------------------------

    /**
     * The location hierarchy filter
     * 
     * @param {String} name                            The name of the filter
     * @param {Array[String]} ids                      The IDs that the filter applies to
     * @param {HierarchyFilter} children       The children of the location hierarchy filter
     * @param {AbstractFilterGroup} group              The filter group
     */
    function HierarchyFilter(name, id, filters) {
        HierarchyFilter.__super__.constructor.apply(this, [name]);
        this.checked = false;
        this.filters = filters;
        this._id = id;
        this._resetCount();
    }
    extend(HierarchyFilter, AbstractFilter);

    /**
     * Sets the filter group
     *
     * @param {FilterGroup} group the filter group
     * @return {Filter} the filter
     */
    HierarchyFilter.prototype._setGroup = function(group) {
        this.filters.forEach(function(filter) {
            filter._setGroup(group);
        });
        HierarchyFilter.__super__._setGroup.apply(this, arguments);
    };

    /**
     * Notifies the mediator of a filter change
     * 
     * @return {Filter} the filter
     */
    HierarchyFilter.prototype.change = function() {
        this._checkAll(this.checked);
        return HierarchyFilter.__super__.change.apply(this, arguments);
    };

    /**
     * Visits each node in the tree
     * 
     * @param {Function} visitor the visitor function
     * @return {Function} the visitor function
     */
    HierarchyFilter.prototype.visit = function(visitor) {
        visitor.acceptFilter && visitor.acceptFilter(this);
        this.filters.forEach(function(filter) {
            filter.visit(visitor);
        });
        return visitor;
    };    

    /**
     * Resets the filter
     * 
     * @return {CheckBoxFilter} the filter
     */
    HierarchyFilter.prototype._reset = function() {
        this._checkAll(false);
        this._resetCount();
        return HierarchyFilter.__super__._reset.apply(this, arguments);
    };

    /**
     * Collects all the IDs for the filter group
     * 
     * @return {Array[String]} the IDs for the filter group
     */
    HierarchyFilter.prototype._collect = function() {
        return this.filters.reduce(function(collector, filter) {
            return collector.concat(filter._collect());
        }, this.checked && this.filters.length === 0 ? this._id : []);
    };

    /**
     * Determines if the filter is applied
     * 
     * @return {Boolean} true if the filter is applied, false otherwise
     */
    HierarchyFilter.prototype._isFilterApplied = function() {
        return this.checked || this.filters.some(function(filter) {
            return filter._isFilterApplied();
        });
    };

    /**
     * Calculates the filtered count
     * 
     * @param  {Array[String]} filteredIds the filtered IDs
     * @return {CheckBoxFilter} the filter
     */
    HierarchyFilter.prototype._calculate = function(filteredIds) {
        var childLocationCount = this.filters.map(function(filter) {
            return filter._calculate(filteredIds);
        });
        var count = this._id.reduce(function (acc, next) {
            return acc + (filteredIds.indexOf(next) > -1 ? 1 : 0);
        }, 0);
        this.filteredCount = childLocationCount.reduce(function(total, value) {
            return total + value;
        }, count);
        return this.filteredCount;
    };

    /**
     * Checks the filter and all children
     *
     * @param {Boolean} checked whether the filter is checked or not
     * @return {Filter} the filter
     */
    HierarchyFilter.prototype._checkAll = function(checked) {
        this.checked = checked;
        this.filters.forEach(function(filter) {
            filter._checkAll(checked);
        });
        return this;
    };

    /**
     * Calculate child location count
     *
     * @return {Number} the total count of child locations under this location
     */
    HierarchyFilter.prototype._resetCount = function() {
        var childLocationCount = this.filters.map(function(filter) {
            return filter._resetCount();
        });
        this.count = childLocationCount.reduce(function(total, value) {
            return total + value;
        }, this.filters.length === 0 ? this._id.length : 0);
        this.filteredCount = this.count;
        return this.count;
    };


    // -------------------------------------------------------------------------------------------
    // Text Filter Group
    // -------------------------------------------------------------------------------------------

    /**
     * The text filter
     * 
     * @param {Array[String]} ids the IDs that the filter applies to
     * @param {Object} an object, mapping names to IDs
     */
    function TextFilter(textMap) {
        TextFilter.__super__.constructor.apply(this, [""]);
        this.text = "";
        this._textMap = textMap;
    }
    extend(TextFilter, AbstractFilter);

    /**
     * Resets the filter
     * 
     * @return {TextFilter} the filter
     */
    TextFilter.prototype._reset = function() {
        this.text = "";
        return TextFilter.__super__._reset.apply(this, arguments);
    };

    /**
     * Collects all the IDs for the filter group
     * 
     * @return {Array[String]} the IDs for the filter group
     */
    TextFilter.prototype._collect = function() {
        var textMap = this._textMap, text = this.text.toLowerCase();
        var ids = Object.keys(textMap).map(function(name) {
            return name.toLowerCase().indexOf(text) > -1 ? [textMap[name]] : [];
        });
        return ids.reduce(function(collector, id) {
            return collector.concat(id);
        }, []);
    };

    /**
     * Determines if the filter is applied
     * 
     * @return {Boolean} true if the filter is applied, false otherwise
     */
    TextFilter.prototype._isFilterApplied = function() {
        return this.text !== "";
    };


    // -------------------------------------------------------------------------------------------
    // Text Filter Group
    // -------------------------------------------------------------------------------------------

    /**
     * The filter group mediator
     * 
     */
    function FilterMediator(groups) {
        this._batch = false;
        this.groups = groups;
        this.filteredCount = 0;
        this._listeners = [];
    }

    /**
     * The filter events
     * 
     * @enum the filter event names
     */
    FilterMediator.Events = {};
    FilterMediator.Events.CHANGE = "change";
    FilterMediator.Events.RESET = "reset";

    /**
     * Resets the filter
     * 
     * @return {FilterMediator} the filter mediator
     */
    FilterMediator.prototype.reset = function() {
        this.groups.forEach(function(group) {
            return group.reset();
        });
        this.filteredCount = 0;
        return this._notify(FilterMediator.Events.RESET);
    };

    /**
     * Determines if the filter is applied
     * 
     * @return {Boolean} true if the filter is applied, false otherwise
     */
    FilterMediator.prototype.isFilterApplied = function() {
        return this.groups.some(function(group) {
            return group.isFilterApplied();
        });
    };

    /**
     * Adds a listener
     * 
     * @param {Function} listener the listener
     * @return {FilterMediator}   the filter mediator
     */
    FilterMediator.prototype.addListener = function(listener) {
        this._listeners.push(listener);
        return this;
    };

    /**
     * Removes a listener
     * 
     * @param {Function} listener the listener
     * @return {FilterMediator}   the filter mediator
     */
    FilterMediator.prototype.removeListener = function(listener) {
        var index = this._listeners.indexOf(listener);
        if (index > -1) {
            this._listeners.splice(index, 1);
        }
        return this;
    };

    /**
     * Removes the listeners
     * 
     * @return {FilterMediator} the filter mediator
     */
    FilterMediator.prototype.removeListeners = function() {
        this._listeners = [];
        return this;
    };

    /**
     * Visits each node in the tree
     * 
     * @param {Function} visitor the visitor function
     * @return {Function} the visitor function
     */
    FilterMediator.prototype.visit = function(visitor) {
        this.groups.forEach(function(group) {
            group.visit(visitor);
        });
        return visitor;
    };

    /**
     * Performs the recalculation of the filters
     *
     * @return {FilterMediator} the filter mediator
     */
    FilterMediator.prototype.change = function() {
        this[this.isFilterApplied() ? "_calculate" : "reset"]();
        return this;
    };

    /**
     * Sets the batch mode, if batch mode is enabled, the filters can
     * be changed but no listeners will be notified until the batch mode
     * has been disabled
     *
     * @param {Boolean} enableBatch true to enable the batch mode, false otherwise
     * @return {FilterMediator} the filter mediator
     */
    FilterMediator.prototype.setBatchMode = function(enableBatch) {
        this._batch = enableBatch;
        this[this.isFilterApplied() ? "_calculate" : "reset"]();
        return this;
    };

    /**
     * Notifies any listeners of a change in the filters
     *
     * @param {String} event              the name of the event
     * @param {Array[String]} filteredIds the filtered IDs
     * @return {FilterMediator}           the filter mediator
     */
    FilterMediator.prototype._notify = function(event, filteredIds) {
        if (!this._batch) {
            var mediator = this;
            this._listeners.forEach(function(listener) {
                listener.call(mediator, event, filteredIds);
            });
        }
        return this;
    };

    /**
     * Collects all the filtered hotel IDs
     * 
     * @return {Array[String]} the filtered IDs
     */
    FilterMediator.prototype._collectFiltered = function() {
        return this.groups.reduce(function(collector, group) {
            return group.isFilterApplied() ? collector.concat([group._collect()]) : collector;
        }, []);
    };

    /**
     * Recalculates the filters based on the selection by the user
     *
     * @return {Array[String]} the filtered hotel IDs
     */
    FilterMediator.prototype._calculate = function() {

        // 1. collect all the IDs from the applied filters
        var filtered = andFilter(this._collectFiltered());

        // 2. update the filters based on the filtered IDs
        this.groups.forEach(function(group) {
            group._calculate(filtered);
        });

        // 3. update the filtered count
        this.filteredCount = filtered.length;

        // 4. fire the change event
        return this._notify(FilterMediator.Events.CHANGE, filtered);
    };


    // -------------------------------------------------------------------------------------------
    // Filter Builder
    // -------------------------------------------------------------------------------------------
    
    /**
     * The filter builder
     * 
     */
    function FilterBuilder() {
        this._builders = [];
    }

    /**
     * Adds a CheckBox filter group to the builder
     *
     * @param {String} name The name of the filter group
     */
    FilterBuilder.prototype.addCheckBoxFilters = function(name, mode) {
        return this.addCheckBoxFiltersOfType("check", name, mode);
    };

    /**
     * Adds a CheckBox filter group to the builder
     *
     * @param {String} name The name of the filter group
     */
    FilterBuilder.prototype.addCheckBoxFiltersOfType = function(type, name, mode) {
        this._builders.push(new CheckBoxFilterGroupBuilder(type, name, mode));
        return this._builders[this._builders.length - 1];
    };

    /**
     * Adds a LocationHierarchy filter group to the builder
     *
     * @param {String} name The name of the filter group
     */
    FilterBuilder.prototype.addHierarchyFilters = function(name) {
        this._builders.push(new HierarchyFilterGroupBuilder(name));
        return this._builders[this._builders.length - 1];
    };

    /**
     * Adds a text filter group to the builder
     *
     * @param {String} name The name of the filter group
     */
    FilterBuilder.prototype.addTextFilter = function(name) {
        this._builders.push(new TextFilterGroupBuilder(name));
        return this._builders[this._builders.length - 1];
    };

    /**
     * Builds the filter group
     * 
     * @return {FilterMediator} the filter object model
     */
    FilterBuilder.prototype.build = function() {
        var mediator = new FilterMediator(this._builders.map(function(builder) {
            return builder._build();
        }));
        // set up the association, groups know about the mediator
        mediator.groups.forEach(function(group) {
            group._setMediator(mediator);
            // and filters know about the group they are in
            group.filters.forEach(function(filter) {
                filter._setGroup(group);
            });
        });
        // the filters are built, and ready to bind to the view
        return mediator;
    };


    // -------------------------------------------------------------------------------------------
    // Abstract Filter Group Builder
    // -------------------------------------------------------------------------------------------
    
    /**
     * The abstract filter group builder
     * 
     * @param {String} type the filter group type
     * @param {String} name the filter group name
     * @param {String} mode the filter group mode
     */
    function AbstractFilterGroupBuilder(type, name, mode) {
        this._type = type;
        this._name = name;
        this._mode = mode = mode != null ? mode : FilterGroup.MODE.OR;
        this._filters = [];
    }


    // -------------------------------------------------------------------------------------------
    // CheckBox Filter Group Builder
    // -------------------------------------------------------------------------------------------
    
    /**
     * The CheckBox filter group builder
     *
     * @param {String} type the filter group type
     * @param {String} name the filter group name
     * @param {String} mode the filter group mode
     */
    function CheckBoxFilterGroupBuilder(type, name, mode) {
        CheckBoxFilterGroupBuilder.__super__.constructor.apply(this, [type, name, mode]);
    }
    extend(CheckBoxFilterGroupBuilder, AbstractFilterGroupBuilder);
    
    /**
     * Adds CheckBox filter with a name and the IDs which the filter applies to
     * 
     * @param {Strign} name the name of filter
     * @param {Array[String]} ids The IDs that the filter applies to
     */
    CheckBoxFilterGroupBuilder.prototype.addFilter = function(name, ids) {
        this._filters.push(new CheckBoxFilter(name, ids));
        return this;
    };

    /**
     * Builds the filter group
     * 
     * @return {FilterGroup} the filter group
     */
    CheckBoxFilterGroupBuilder.prototype._build = function() {
        return new FilterGroup(this._type, this._name, this._mode, this._filters);
    };


    // -------------------------------------------------------------------------------------------
    // Text Filter Group Builder
    // -------------------------------------------------------------------------------------------

    /**
     * The TextBox filter group builder
     *
     * @param {String} name the name of the filter group
     */
    function TextFilterGroupBuilder(name) {
        TextFilterGroupBuilder.__super__.constructor.apply(this, ["text", name]);
    }
    extend(TextFilterGroupBuilder, AbstractFilterGroupBuilder);
    
    /**
     * Adds a text filter with a name
     * 
     * @param {Object} an object, mapping names to IDs
     * @return {FilterGroup} the filter group
     */
    TextFilterGroupBuilder.prototype.setFilter = function(textMap) {
        this._filters = Object.keys(textMap).length > 0 ? [new TextFilter(textMap)] : [];
        return this;
    };

    /**
     * Builds the filter group
     * 
     * @return {FilterGroup} the filter group
     */
    TextFilterGroupBuilder.prototype._build = function() {
        return new FilterGroup(this._type, this._name, this._mode, this._filters);
    };


    // -------------------------------------------------------------------------------------------
    // Location Hierarchy Filter Group Builder
    // -------------------------------------------------------------------------------------------
    
    /**
     * The Location Hierarchy Filter Group Builder
     * 
     * @param {String} name the name of the filter group
     */
    function HierarchyFilterGroupBuilder(name) {
        HierarchyFilterGroupBuilder.__super__.constructor.apply(this, ["hierarchy", name]);
    }
    extend(HierarchyFilterGroupBuilder, AbstractFilterGroupBuilder);
    
    /**
     * Adds a location hierarchy filter
     * 
     * @param {String} name the name of the filter
     * @param {String} id the ID the filter applies to
     */
    HierarchyFilterGroupBuilder.prototype.addFilter = function(name, id) {
        this._filters.push(new HierarchyFilterBuilder(name, id));
        return this._filters[this._filters.length - 1];
    };

    /**
     * Builds the filter group
     * 
     * @return {FilterGroup} the filter group
     */
    HierarchyFilterGroupBuilder.prototype._build = function() {
        return new HierarchyFilterGroup(this._type, this._name, this._mode, this._filters.map(function(child) {
            return child._build();
        }));
    };

    // -------------------------------------------------------------------------------------------
    // Location Hierarchy Filter Builder
    // -------------------------------------------------------------------------------------------
    
    /**
     * The location hiearchy filter builder
     * 
     * @param {String} name the name of the filter
     * @param {String} id the ID the filter applies to
     */
    function HierarchyFilterBuilder(name, id) {
        this._name = name;
        this._id = id;
        this._filters = [];
    }

    /**
     * Adds a child location
     * 
     * @param {String} name the name of the filter
     * @param {String} id the ID the filter applies to
     */
    HierarchyFilterBuilder.prototype.addFilter = function(name, id) {
        this._filters.push(new HierarchyFilterBuilder(name, id));
        return this._filters[this._filters.length - 1];
    };

    /**
     * Builds the filters
     * 
     * @return {FilterGroup} the filters
     */
    HierarchyFilterBuilder.prototype._build = function() {
        return new HierarchyFilter(this._name, this._id, this._filters.map(function(filter) {
            return filter._build();
        }));
    };


    // -------------------------------------------------------------------------------------------
    // Filter Builder Factory
    // -------------------------------------------------------------------------------------------
    
    module.factory("filterBuilder", [function() {
        var builder = function() {
            return new FilterBuilder();
        };
        builder.MODE = FilterGroup.MODE;
        return builder;
    }]);

})(angular.module("vhols-filters", []));

;(function(){

'use strict';

angular.module('vhols-filters').run(['$templateCache', function($templateCache) {

  $templateCache.put('checkbox-filter.html', '<div class="form check-panel">\n    <div class="checkbox">\n        <label ng-class="{ \'ticked\': group.isFilterApplied() == false }" ng-click="group.reset(); group.change();">\n            <span class="tick"></span>\n            Show all\n        </label>\n    </div>\n    <div class="checkbox" ng-repeat="filter in group.filters">\n        <label ng-class="{ \'text-muted\': filter.filteredCount === 0, \'ticked\': filter.checked }">\n            <span class="tick"></span>\n            <input type="checkbox"\n                   ng-model="filter.checked"\n                   ng-change="filter.change()"/>\n            {{filter.name}}\n            <span ng-show="filter.filteredCount || filter.checked">({{filter.filteredCount}})</span>\n        </label>\n    </div>\n</div>');

  $templateCache.put('hierarchy-filter.html', '<div class="hierarchy-filter" ng-if="filter.filters.length &gt; 0">\n    <label ng-class="{ \'text-muted\': filter.filteredCount === 0, \'ticked\': filter.checked }">\n        <span class="tick"></span>\n        <input type="checkbox"\n               ng-model="filter.checked"\n               ng-change="filter.change()"/>\n        <div class="indent">\n            {{filter.name}}\n            <span ng-show="filter.filteredCount || filter.checked">({{filter.filteredCount}})</span>\n        </div>\n    </label>\n    <div ng-repeat="filter in filter.filters" ng-include="\'hierarchy-filter.html\'"></div>\n</div>\n');

  $templateCache.put('text-filter.html', '<div class="form-group">\n    <input type="text"\n       ng-model="group.filters[0].text"\n       ng-change="group.change()"\n       class="form-control"\n       placeholder="Filter by name"\n       value="{{name}}">\n   <span ng-click="group.reset(); group.change();"\n         ng-show="group.isFilterApplied();"\n         class="reset-text">X</span>\n</div>');

}]);

})();